import { useContext, useEffect, useState } from "react";

import { FileUploadingContext } from "@/features/project-file-uploading";
import { useFileTextQuery } from "@/queries/query-file-text";
import { ProjectContext } from "@/stores/project-provider";
import { ProjectFile } from "@/types/api-model-simple-names";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

export function useFilePreviewEdit(file: ProjectFile) {
  const { filesBeingUploaded } = useContext(FileUploadingContext);
  const { id: projectId } = useContext(ProjectContext);
  const { uploadFiles } = useContext(FileUploadingContext);

  const { data: textFileContent, isLoading, isSuccess } = useFileTextQuery(file);

  const [text, setText] = useState(textFileContent);
  useEffect(() => {
    setText(textFileContent);
  }, [textFileContent]);

  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const fileIsBeingUploaded = filesBeingUploaded.find(
    (uploading) =>
      uploading.file.name === getNameFromFileId(file.id) && uploading.status === "loading"
  );

  const editTextFile = (text: string) => {
    const blob = new File([text], getNameFromFileId(file.id), {
      type: "text/plain",
      lastModified: Date.now(),
    });

    const directory = (() => {
      const splitPath = file.id.split("/");
      if (splitPath.length === 1) {
        return "";
      } else {
        return splitPath.slice(0, -1).join("/") + "/";
      }
    })();

    uploadFiles({
      projectId,
      uploads: [
        {
          directory,
          files: [{ file: blob, dataType: file.dataType ?? undefined }],
        },
      ],
      withoutProgressDialog: true,
    });
  };

  return {
    isLoading,
    isSuccess,
    fileIsBeingUploaded,
    text,
    setText,
    isEditing,
    isPreviewing,

    startEditing: () => {
      setIsEditing(true);
      setIsPreviewing(false);
    },
    startPreviewing: () => setIsPreviewing(true),
    cancelEditing: () => {
      setIsEditing(false);
      setText(textFileContent);
    },
    submit: () => {
      if (text) editTextFile(text);
      setIsEditing(false);
    },
  };
}
